import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Shield as ShieldIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { vortxColors, formatCurrency } from '@vortx/shared-ui';

const RISK_GRADE_COLORS = {
  A: vortxColors.teal,
  B: vortxColors.gold,
  C: '#ff9800',
  D: vortxColors.magenta,
};

const RISK_GRADE_LABELS = {
  A: 'Low Risk',
  B: 'Moderate Risk',
  C: 'Elevated Risk',
  D: 'High Risk',
};

/**
 * GuaranteeScoreCard — displays P50/P75/P90 savings and risk profile
 * for a proposal. Integrates into the deal analysis flow.
 *
 * Props:
 *   scoreData: {
 *     p50_savings_pct: number,
 *     p75_savings_pct: number,
 *     p90_savings_pct: number,
 *     risk_grade: 'A'|'B'|'C'|'D',
 *     risk_score: number (0-1),
 *     band_width_pp: number,
 *     risk_factors: string[],
 *   }
 *   loading: boolean
 *   onRequestContract: () => void (optional — callback to initiate contract)
 *   annualSavingsDollars: number (optional — show dollar amounts alongside percentages)
 *   baselineCost: number (optional)
 *   optimizedCost: number (optional)
 *   simulationBacked: boolean (optional — show "Simulation-Backed" badge)
 */
export default function GuaranteeScoreCard({
  scoreData,
  loading,
  onRequestContract,
  annualSavingsDollars,
  baselineCost,
  optimizedCost,
  simulationBacked = false,
}) {
  if (loading) {
    return (
      <Card sx={{ bgcolor: 'background.paper', border: `1px solid ${vortxColors.teal}33` }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <ShieldIcon sx={{ color: vortxColors.teal }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Performance Guarantee
            </Typography>
          </Stack>
          <LinearProgress sx={{ '& .MuiLinearProgress-bar': { bgcolor: vortxColors.teal } }} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Scoring scenario...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!scoreData) {
    return null;
  }

  const {
    p50_savings_pct,
    p75_savings_pct,
    p90_savings_pct,
    risk_grade,
    risk_score,
    band_width_pp,
    risk_factors = [],
  } = scoreData;

  const gradeColor = RISK_GRADE_COLORS[risk_grade] || vortxColors.gold;

  return (
    <Card sx={{ bgcolor: 'background.paper', border: `1px solid ${vortxColors.teal}33` }}>
      <CardContent>
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ShieldIcon sx={{ color: vortxColors.teal }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Performance Guarantee
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            {simulationBacked && (
              <Chip
                label="Simulation-Backed"
                size="small"
                sx={{
                  bgcolor: `${vortxColors.teal}22`,
                  color: vortxColors.teal,
                  fontWeight: 600,
                  border: `1px solid ${vortxColors.teal}44`,
                }}
              />
            )}
            <Chip
              label={`Grade ${risk_grade} — ${RISK_GRADE_LABELS[risk_grade]}`}
              size="small"
              sx={{
                bgcolor: `${gradeColor}22`,
                color: gradeColor,
                fontWeight: 600,
                border: `1px solid ${gradeColor}44`,
              }}
            />
          </Stack>
        </Stack>

        {/* Savings bands */}
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={3} justifyContent="space-between">
            <SavingsBand
              label="Expected (P50)"
              value={p50_savings_pct}
              color={vortxColors.teal}
              isPrimary={false}
            />
            <SavingsBand
              label="Likely (P75)"
              value={p75_savings_pct}
              color={vortxColors.gold}
              isPrimary={false}
            />
            <SavingsBand
              label="Guaranteed (P90)"
              value={p90_savings_pct}
              color={vortxColors.magenta}
              isPrimary={true}
            />
          </Stack>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Guarantee floor callout */}
        <Box
          sx={{
            bgcolor: `${vortxColors.teal}11`,
            border: `1px solid ${vortxColors.teal}33`,
            borderRadius: 1,
            p: 1.5,
            mb: 1.5,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <TrendingUpIcon sx={{ color: vortxColors.teal, fontSize: 20 }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Guaranteed minimum savings: {p90_savings_pct?.toFixed(1)}%
            </Typography>
            <Tooltip title="90% of sites with this profile achieve at least this savings level. Backed by active AI optimization and contractual commitment.">
              <InfoIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
          </Stack>
        </Box>

        {/* Dollar amounts (when available) */}
        {annualSavingsDollars != null && (
          <Box sx={{ mb: 1.5 }}>
            <Stack direction="row" spacing={3} justifyContent="space-between">
              <Box sx={{ textAlign: 'center', flex: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25, fontSize: '0.7rem' }}>
                  Projected Annual Savings
                </Typography>
                <Typography variant="h6" sx={{ color: vortxColors.teal, fontWeight: 600 }}>
                  {formatCurrency(annualSavingsDollars)}
                </Typography>
              </Box>
              {baselineCost != null && (
                <Box sx={{ textAlign: 'center', flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25, fontSize: '0.7rem' }}>
                    Baseline Cost
                  </Typography>
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                    {formatCurrency(baselineCost)}
                  </Typography>
                </Box>
              )}
              {optimizedCost != null && (
                <Box sx={{ textAlign: 'center', flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25, fontSize: '0.7rem' }}>
                    Optimized Cost
                  </Typography>
                  <Typography variant="h6" sx={{ color: vortxColors.gold, fontWeight: 600 }}>
                    {formatCurrency(optimizedCost)}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>
        )}

        {/* Risk factors */}
        {risk_factors.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
              Risk Factors
            </Typography>
            {risk_factors.map((factor, i) => (
              <Stack key={i} direction="row" alignItems="flex-start" spacing={0.5} sx={{ mb: 0.25 }}>
                <WarningIcon sx={{ fontSize: 14, color: gradeColor, mt: 0.25 }} />
                <Typography variant="caption" color="text.secondary">
                  {factor}
                </Typography>
              </Stack>
            ))}
          </Box>
        )}

        {/* Band width info */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Confidence band: ±{band_width_pp?.toFixed(1)}pp
          {' · '}
          Risk score: {(risk_score * 100)?.toFixed(0)}%
        </Typography>
      </CardContent>
    </Card>
  );
}

function SavingsBand({ label, value, color, isPrimary }) {
  return (
    <Box sx={{ textAlign: 'center', flex: 1 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: 0.25, fontSize: '0.7rem' }}
      >
        {label}
      </Typography>
      <Typography
        variant={isPrimary ? 'h4' : 'h5'}
        sx={{
          color,
          fontWeight: isPrimary ? 700 : 600,
          lineHeight: 1,
        }}
      >
        {value?.toFixed(1)}%
      </Typography>
    </Box>
  );
}
